"use client"

import styled from "@emotion/styled"
import Container from "@eurodrive/ui/Container"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { Typography } from "@mui/material"
import Image from "next/image"
import Link from "next/link"

interface CategoryChild {
  name: string
  classification: string
  image: string
}

interface CategoriesProps {
  categoryChildren: CategoryChild[]
  manufacturerCode: number
}

function parseClassification(classification: string, fallbackLabel: string) {
  const value = String(classification || "").trim()

  if (!value) {
    return null
  }

  const match = value.match(/^(\d+)\s*-\s*(.+)$/)
  const typeCode = Number(match?.[1] || value)

  if (!Number.isFinite(typeCode)) {
    return null
  }

  return {
    label: match?.[2]?.trim() || fallbackLabel,
    typeCode,
  }
}

const StyledSection = styled.div`
  padding: 56px 0;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("sm")} {
    padding: 64px 0;
  }
`

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 19px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StyledCard = styled(Link)`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  border: 1px solid #ece9e9;
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 233px;
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
`

const LabelContainer = styled.div`
  padding: 16px 32px;
  text-align: end;
  background-color: #fff;
`

export default function Categories({
  categoryChildren,
  manufacturerCode,
}: CategoriesProps) {
  const categoriesByTypeCode = new Map<
    number,
    {
      label: string
      image: string
      typeCode: number
    }
  >()

  categoryChildren.forEach((child) => {
    const classification = parseClassification(child.classification, child.name)

    if (!classification || !child.image) {
      return
    }

    categoriesByTypeCode.set(classification.typeCode, {
      ...classification,
      image: child.image,
    })
  })

  const categories = Array.from(categoriesByTypeCode.values())

  if (categories.length === 0) return null

  return (
    <StyledSection>
      <Container paddingMobile>
        <StyledGrid>
          {categories.map((category) => (
            <StyledCard
              key={category.typeCode}
              href={`/search?manufacturer=${manufacturerCode}&carTypes=${category.typeCode}`}
            >
              <ImageContainer>
                <Image
                  src={getUmbracoMediaUrl(category.image)}
                  alt={category.label}
                  fill
                  sizes="(max-width: 600px) 50vw, 33vw"
                  style={{ objectFit: "cover" }}
                />
              </ImageContainer>
              <LabelContainer>
                <Typography variant="body1" fontWeight={600}>
                  {category.label}
                </Typography>
              </LabelContainer>
            </StyledCard>
          ))}
        </StyledGrid>
      </Container>
    </StyledSection>
  )
}
